import type { AccountDetails } from "@/apis/gql/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ButtonProps } from "../../ui/button";
import { Dialog, DialogContent } from "../../ui/dialog";
import { NewAccountForm } from "../form/new-account";
import { ComboBox, type ComboOption } from "../tables/tableComboBox";

enum SpecialFilterValue {
  Unknown = "unknown",
}

export type Props = {
  currentOption?: ComboOption;
  onChange: (value: ComboOption["value"]) => void;
  onFocus?: () => void;
  items: AccountDetails[];
  variant?: ButtonProps["variant"];
  allowUnknownAccount?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  className?: ButtonProps["className"];
  disabled?: boolean | string | React.ReactNode;
  fullWidth?: boolean;
  side?: "top" | "bottom";
  sideOffset?: number;
  container?: React.RefObject<HTMLElement>;
  readOnly?: boolean | string | React.ReactNode;
  modal?: boolean;
};

export const AccountsDropdown = ({
  currentOption,
  onChange,
  onFocus,
  items,
  variant,
  placeholder = "Sachkonto",
  allowUnknownAccount = false,
  className,
  disabled,
  fullWidth,
  sideOffset,
  container,
  side,
  readOnly,
  modal = false,
}: Props) => {
  const [newAccountDialogOpen, setNewAccountDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { t } = useTranslation();

  const handleCreateNew = (searchTerm: string) => {
    setSearchValue(searchTerm);
    setNewAccountDialogOpen(true);
  };

  const accountOptions = items.map(
    (account): ComboOption => ({
      label: account.accountName,
      alternativeLabels: [account.accountNo],
      value: account.accountId,
      subLabel: account.accountNo,
    }),
  );

  allowUnknownAccount &&
    accountOptions.push({
      label: "Unbekannt",
      value: SpecialFilterValue.Unknown,
      alternativeLabels: [],
      subLabel: "Sachkonto",
    });

  return (
    <>
      <ComboBox
        readOnly={readOnly}
        disabled={disabled}
        currentOption={currentOption}
        onChange={onChange}
        items={accountOptions ?? []}
        placeholder={placeholder}
        createNew={{
          label: t("accounts.new_title"),
          onCreateNew: handleCreateNew,
        }}
        emptyLabel={t("accounts.no_accounts_found")}
        variant={variant}
        className={className}
        popoverAlignStart
        fullWidth={fullWidth}
        onFocus={onFocus}
        sideOffset={sideOffset}
        side={side}
        container={container}
        modal={modal}
      />
      <Dialog
        open={newAccountDialogOpen}
        onOpenChange={setNewAccountDialogOpen}
      >
        <DialogContent className="sm:max-w-[425px] z-index-[100]">
          <NewAccountForm
            initialValues={{
              accountNo: /^-?\d+$/.test(searchValue) ? searchValue : "",
              accountName:
                /^-?\d+$/.test(searchValue) === false ? searchValue : "",
              accountDescription: "",
            }}
            onSuccess={(accountId) => {
              setNewAccountDialogOpen(false);
              setSearchValue("");
              onChange(accountId);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
